import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MenuBarItem';
import Menu2LineBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/Menu2LineBarItem';
import RankingProductSection from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/RankingProductSection';
import { EventProduct } from '@wconcept/wconcept-common/models/event/custom';
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import useGa4Event from '@hooks/useGa4Event';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { useCategoryRankProducts } from '../hooks/useCategoryRankProducts';

interface CategoryItem {
  categoryName: string;
  displayCategoryName: string;
  key: string;
  mediumName: string;
}

interface ProductRenderParams {
  products: EventProduct[];
  type: 'simple' | 'list';
  maxCount: number;
  useRank: { page: number };
  colorType?: 'white' | 'black';
  isBrandNameKr?: boolean;
  rs?: string;
  onClickProduct?: (product: EventProduct, index: number) => void;
  onClickOption?: (product: EventProduct, index: number) => void;
}

interface RankingModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  categoryList: CategoryItem[];
  rankCateTab: CategoryItem;
  subTitle?: string;
  title?: string;
  order: number;
  promotionTitle?: string;
  onHeartClick?: (product: EventProduct, isAdd: boolean, index: number) => void;
  renderProducts: (params: ProductRenderParams) => React.ReactNode[];
  registerModule?: (contentId: string, element: HTMLElement | null) => void;
  contentId?: string;
  promotionId?: number;
  selectedTab: CategoryItem | null;
  setSelectedTab: (tab: CategoryItem) => void;
}

export function RankingModule({
  bgType,
  visualTokens,
  isDesktop,
  categoryList,
  rankCateTab,
  subTitle,
  contentId,
  promotionTitle,
  title,
  promotionId,
  order,
  onHeartClick,
  renderProducts,
  registerModule,
  selectedTab,
  setSelectedTab,
}: RankingModuleProps) {
  const INIT_COUNT = isDesktop ? 12 : 40;
  const MAX_COUNT = isDesktop ? 30 : 40;
  const [maxCount, setMaxCount] = useState<number>(INIT_COUNT);

  const {
    presetBgType,
    customColorsFull,
    productUnitColorType: promotionProductUnitColorType,
    productSkeletonBgType: promotionProductSkeletonBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType = customColorsFull ?? presetBgType;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [updateTime, setUpdateTime] = useState<string>('');
  const { sendGa4SelectItem, sendGa4TabClick, sendGa4ButtonClick } =
    useGa4Event();
  // React Query를 사용한 데이터 페칭
  const {
    isLoading,
    data: selectedCategoryProducts = { data: [], baseData: { updateTime: '' } },
  } = useCategoryRankProducts({
    categoryKey: selectedTab?.key || '',
    enabled: !!selectedTab?.key,
  });
  const handleTabClick = useCallback(
    (tab: CategoryItem, i: number) => {
      sendGa4TabClick({
        area: 'promotion_ranking_tab',
        issueCd: promotionId?.toString(),
        issueNm: promotionTitle,
        text: tab.displayCategoryName,
        listNo: Number(i + 1),
      });
      setSelectedTab(tab);
      setMaxCount(INIT_COUNT);
      // 모바일에서 탭 변경 시 스크롤 초기화
      if (!isDesktop) {
        scrollRef.current?.scrollTo({ left: 0 });
      }
    },
    [
      sendGa4TabClick,
      promotionId,
      promotionTitle,
      setSelectedTab,
      isDesktop,
      INIT_COUNT,
    ]
  );
  const getCategoryMenuList = useCallback(
    () =>
      categoryList?.map((v, i) =>
        isDesktop ? (
          <Menu2LineBarItem
            key={v.key}
            value={v.key}
            type="type2"
            text={v.displayCategoryName}
            selectedValue={selectedTab?.key}
            onClick={() => handleTabClick(v, i)}
          />
        ) : (
          <MenuBarItem
            key={v.key}
            value={v.key}
            text={v.displayCategoryName}
            onClick={() => handleTabClick(v, i)}
          />
        )
      ),
    [categoryList, isDesktop, selectedTab, handleTabClick]
  );

  const categoryProducts = useMemo(
    () => selectedCategoryProducts.data.map(v => v.product),
    [selectedCategoryProducts]
  );

  const renderedProducts = useMemo(() => {
    if (!isDesktop && isLoading) {
      return Array.from({ length: maxCount }, (_, index) => (
        <PrdItemSkeleton key={index} bgType={promotionProductSkeletonBgType} />
      ));
    }
    return renderProducts({
      products: categoryProducts,
      type: isDesktop ? 'simple' : 'list',
      isBrandNameKr: true,
      maxCount,
      useRank: { page: 0 },
      rs: isDesktop ? '417' : '216',
      colorType: promotionProductUnitColorType,
      onClickOption: (product, index) => {
        sendGa4SelectItem({
          area: 'promotion_ranking_item',
          page: 'promotion',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          listNo: Number(index + 1),
          ecommercePath: '프로모션',
          product,
          tabNm: selectedTab?.displayCategoryName || '',
        });
      },
    });
  }, [
    isDesktop,
    isLoading,
    renderProducts,
    categoryProducts,
    maxCount,
    promotionProductSkeletonBgType,
    promotionProductUnitColorType,
    sendGa4SelectItem,
    promotionId,
    promotionTitle,
    selectedTab,
  ]);

  const showMoreButton =
    categoryProducts.length > maxCount && maxCount < MAX_COUNT;

  const onClickMore = useCallback(() => {
    sendGa4ButtonClick({
      area: 'promotion_ranking_more_button',
      text: '상품 더보기',
      page: 'promotion',
      issueCd: promotionId?.toString(),
      issueNm: promotionTitle,
    });
    setMaxCount(MAX_COUNT);
  }, [MAX_COUNT, promotionId, promotionTitle, sendGa4ButtonClick]);

  useEffect(() => {
    setUpdateTime(
      selectedCategoryProducts?.baseData?.updateTime
        ? dayjs(selectedCategoryProducts?.baseData?.updateTime).format('HH:mm')
        : ''
    );
  }, [selectedCategoryProducts]);

  return (
    <RankingProductSection
      title={title}
      subTitle={subTitle}
      colorType={sectionColorType}
      updateTime={updateTime}
      categoryTabList={getCategoryMenuList()}
      selectedCateTabId={selectedTab?.key}
      productList={renderedProducts}
      isDesktop={isDesktop}
      scrollRef={scrollRef}
      onClickMore={onClickMore}
      isLink={false}
      showMoreButton={showMoreButton}
      isLoaded={!isLoading}
    />
  );
}

export default memo(RankingModule);
